<template>
  <div>
    <HeadingSection
      class="!mt-0 mb-[35px]"
      heading="Geofence Types"
      desc="Each custom type sits under a standard type."
      :button="topButton"
    />
    <DataTable
      :loading="isLoading"
      :list="pageRows"
      :columns="columns"
      :edit-actions="editActions"
    />
    <DataTableNav v-model="pagination" :meta="paginationMeta" />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { deleteAccountPlaceType } from '@gpx-spa/queries';
import DataTable from '@gpx-spa/ui/src/elements/DataTable.vue';
import DataTableNav from '@gpx-spa/ui/src/elements/DataTableNav.vue';
import { TableResultsPerPage } from '@gpx-spa/constants';
import HeadingSection from '@/components/_common/Heading/HeadingSection.vue';
import { usePlaceTypes } from '@/composables/usePlaceTypes';

const props = defineProps({
  accountId: {
    type: Number,
    required: true,
  },
});

const store = useStore();
const { placeTypes, options, isLoading } = usePlaceTypes();
const deleteMutation = deleteAccountPlaceType();

const bySlug = computed(
  () => new Map(placeTypes.value.map((type) => [type.slug, type])),
);

const columns = ['Name', 'Type', 'Geofences'];

const rows = computed(() =>
  options.value.map(({ value }) => {
    const type = bySlug.value.get(value);
    return type.is_custom
      ? {
          id: type.id,
          name: [type.name, `Under ${bySlug.value.get(type.parent)?.name}`],
          type: 'Custom',
          geofences: type.geofence_count ?? 0,
          edit: true,
        }
      : {
          id: type.id,
          name: type.name,
          type: 'Standard',
          geofences: '—',
          edit: false,
        };
  }),
);

const pagination = ref({ page: 1, per_page: TableResultsPerPage[0] });

const paginationMeta = computed(() => {
  const total = rows.value.length;
  const { page, per_page } = pagination.value;

  return {
    current_page: page,
    last_page: Math.max(1, Math.ceil(total / per_page)),
    per_page,
    from: total === 0 ? 0 : (page - 1) * per_page + 1,
    to: Math.min(page * per_page, total),
    total,
  };
});

const pageRows = computed(() => {
  const { page, per_page } = pagination.value;
  return rows.value.slice((page - 1) * per_page, page * per_page);
});

const customType = (id) =>
  placeTypes.value.find((type) => type.is_custom && type.id === id);

const topButton = computed(() => ({
  label: 'Add new',
  action: { mode: 'callback', callback: openCreate },
}));

const openCreate = () => {
  store.dispatch('modal/open', { name: 'ManagePlaceTypeModal' });
};

const openEdit = (id) => {
  const type = customType(id);
  if (!type) return;

  store.dispatch('modal/open', {
    name: 'ManagePlaceTypeModal',
    params: { id: type.id, name: type.name, parent: type.parent },
  });
};

const openDelete = (id) => {
  const type = customType(id);
  if (!type) return;

  const parentName = bySlug.value.get(type.parent)?.name;
  const count = type.geofence_count ?? 0;
  const body = count
    ? `${count === 1 ? '1 geofence uses' : `${count} geofences use`} this type. ${count === 1 ? 'It' : 'They'} will move to ${parentName}.`
    : 'No geofence uses this type.';

  store.dispatch('modal/open', {
    name: 'CommonConfirmationModal',
    params: {
      actionLabel: 'Delete geofence type',
      title: type.name,
      body,
      successMessage: 'Geofence type deleted',
      errorMessage: 'Failed to delete the geofence type.',
      onOk: () =>
        deleteMutation.mutateAsync({ accountId: props.accountId, id: type.id }),
      callback: () => store.dispatch('geofence/fetch'),
    },
  });
};

const editActions = [
  {
    icon: 'PencilSquareIcon',
    label: 'Edit',
    action: { mode: 'fn', fn: openEdit },
  },
  {
    icon: 'XMarkIcon',
    label: 'Delete',
    action: { mode: 'fn', fn: openDelete },
  },
];
</script>
